/* eslint-disable no-unused-vars */
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { LocaleService, ContentZonesService } from '@sm360/widget-commons-core-services';
import { ImageWithFallback } from '@sm360/widget-commons-basic-components';
import { TileDisclaimer } from '@sm360/widget-commons-tiles';
import { PurchaseMethods, Vehicle } from '@sm360/widget-commons-vehicle';
import PaymentDetailRebate from 'commons-vehicle/Vehicle/components/Payment/PaymentDetail/PaymentDetailRebate.jsx';

import envConfig from '../../../config/index.js';

import Badges from './Badges/Badges.jsx';
import LoadingTile from './InventoryUsedLoadingTile.jsx';
import './InventoryUsedTile.scss';

const { Payment: Financing, Price, TileTag, VehicleModelV25, URL, ThirdPartiesCTA } = Vehicle;
const { PurchaseMethodsValues: PURCHASE_METHODS, PurchaseMethodsConstantV25: PURCHASE_METHODS_V25 } = PurchaseMethods;

const InventoryUsedTile = ({
    certifiedText,
    certifiedBadge,
    defaultPicture,
    dealerMake,
    inspectedText,
    inspectedBadge,
    onClick,
    hasCarfaxBadge,
    tradedText,
    tradedBadge,
    vehicle,
    warrantyText,
    warrantyBadge,
    isLoading,
    priceLoading,
    showCarfax,
    displayCreditRate,
    motoInsightUsedEnabled,
    roadsterUsedEnabled,
    eshopUsedEnabled,
    shopClickDriveUsedEnabled,
    roadsterLink,
    dealerCode,
    dealerBAC,
}) => {
    if (isLoading) return <LoadingTile />;
    const lang = LocaleService.getSimpleLocale();
    const selectedPurchaseMethodValue = vehicle.purchaseMethods.getSelectedPurchaseMethodValue();
    const pricesDetails = vehicle.purchaseMethods.getSelectedPurchaseMethodPaymentOption();
    const clearance = !!vehicle.clearance && (
        <div className="car-clearance">{LocaleService.t('VEHICLE_STATUS.CLEARANCE')}</div>
    );

    const term = vehicle.purchaseMethods.getSelectedFinanceTerm();
    const save = vehicle.purchaseMethods.bestIncentives?.total > 0 && (
        <div role="button" tabIndex={0} className="listing-tile-car-save has-warning-background">
            <div className="car-save">
                {LocaleService.t('SAVE_MONEY')}
                <span className="is-bold">
                    {LocaleService.t('VEHICLE_STATUS.PRICING', {
                        price: vehicle.purchaseMethods.bestIncentives.total,
                    })}
                </span>
            </div>
        </div>
    );
    const taxCodes = vehicle.purchaseMethods.getTaxCodesString()
        ? vehicle.purchaseMethods.getTaxCodesString()
        : LocaleService.t('taxes.taxes');

    const dealerRebatesAmount = vehicle?.purchaseMethods?.getDealerRebate();

    const roadsterCZCtaLabel = ContentZonesService.getContentZone('widget.listing.used', 'roadster.cta.label.used')
        ? ContentZonesService.getContentZone('widget.listing.used', 'roadster.cta.label.used').getValue()
        : LocaleService.t('CALL_TO_ACTION.BTN_ROADSTER_TEXT');

    const shopClickDriveCZCtaLabel = ContentZonesService.getContentZone(
        'widget.listing.used',
        'shop.click.drive.cta.label.used'
    )
        ? ContentZonesService.getContentZone('widget.listing.used', 'shop.click.drive.cta.label.used').getValue()
        : LocaleService.t('CALL_TO_ACTION.BTN_SHOP_CLICK_DRIVE_TEXT');

    const seeDetailsCZCtaLabel = ContentZonesService.getContentZone('widget.listing.used', 'see.details.cta.label.used')
        ? ContentZonesService.getContentZone('widget.listing.used', 'see.details.cta.label.used').getValue()
        : LocaleService.t('CALL_TO_ACTION.BTN_SEE_DETAILS_TEXT');

    const hasRetail = vehicle.purchaseMethods?.cashPurchase?.retailDetails?.total > 0;

    const dealerRebates =
        (dealerRebatesAmount > 0 && (
            <PaymentDetailRebate
                amount={dealerRebatesAmount}
                i18nKey="PAYMENTS_REBATES.DEALER_REBATE"
                data={{ 'data-sr': 'Rebate' }}
                iconClass="icon-promo2"
                isUsed
            />
        )) ||
        undefined;

    const isFinanceAvailable =
        selectedPurchaseMethodValue === PURCHASE_METHODS_V25.FINANCE && term && term.totalPayment > 0;
    const isCashAvailable =
        selectedPurchaseMethodValue === PURCHASE_METHODS_V25.CASH && pricesDetails?.retailDetails.total > 0;
    const seeDetailsCta =
        isFinanceAvailable || isCashAvailable ? (
            <button className="see-details-cta sr-button-1" onClick={hasRetail ? onClick : ''}>
                {seeDetailsCZCtaLabel}
            </button>
        ) : null;

    const thirdPartiesCta = (
        <div className="listing-tiles-ctas">
            {seeDetailsCta}

            <ThirdPartiesCTA
                motoInsightEnabled={motoInsightUsedEnabled}
                roadsterEnabled={roadsterUsedEnabled}
                shopClickDriveEnabled={shopClickDriveUsedEnabled}
                eshopEnabled={eshopUsedEnabled}
                roadsterLink={roadsterLink}
                dealerBAC={dealerBAC}
                dealerCode={dealerCode}
                vehicle={vehicle}
                roadsterCZCtaLabel={roadsterCZCtaLabel}
                shopClickDriveCZCtaLabel={shopClickDriveCZCtaLabel}
            />
        </div>
    );

    let paymentMethod;

    if (selectedPurchaseMethodValue === PURCHASE_METHODS_V25.FINANCE) {
        paymentMethod = (
            <div className="finance-payment-summary-section">
                <Financing
                    term={term}
                    purchaseMethods={vehicle.purchaseMethods}
                    taxCodes={taxCodes}
                    isUsed
                    loading={priceLoading}
                    displayCreditRate={displayCreditRate}
                    dealerRebates={dealerRebates}
                />
                {thirdPartiesCta}
            </div>
        );
    } else if (selectedPurchaseMethodValue === PURCHASE_METHODS_V25.CASH) {
        const includedDisclaimer = ContentZonesService.getContentZone(
            'widget.commons',
            'disclaimer.price.included.used.cash'
        )
            ? ContentZonesService.getContentZone('widget.commons', 'disclaimer.price.included.used.cash').getValue()
            : '';

        paymentMethod =
            pricesDetails?.retailDetails.total > 0 ? (
                <div className="cash-payment-summary-section">
                    <div className="payment-rebates">{dealerRebates}</div>
                    <Price
                        amount={pricesDetails?.sellingPrice}
                        taxesDisclaimer={pricesDetails?.taxesDisclaimer}
                        includedDisclaimer={includedDisclaimer}
                        retail={pricesDetails?.retailDetails.total}
                        loading={priceLoading}
                    />
                    {thirdPartiesCta}
                </div>
            ) : (
                <div className="tile-payment-info-cash">
                    <div className="tile-frequency is-bold">
                        {vehicle.purchaseMethods.getPurchaseMethodLabels(LocaleService).verb}
                    </div>
                    <span className="tile-payment-info-unavailable">{LocaleService.t('NOT_AVAILABLE')}</span>
                </div>
            );
    }

    const vehicleImage = vehicle.multimedia?.getMainImage360x270() || defaultPicture;

    const getCZCustomTagContent = () => {
        return ContentZonesService.getContentZone('widget.listing.used', 'custom.tag.content')
            ? ContentZonesService.getContentZone('widget.listing.used', 'custom.tag.content').getValue()
            : '';
    };

    const customTag = (
        <TileTag
            shouldRender={vehicle?.custom && getCZCustomTagContent()}
            background="#f2bf24"
            color="#fff"
            text={getCZCustomTagContent()}
            extraClass="custom-tag"
        />
    );

    const getCZDisclaimer = () => {
        return ContentZonesService.getContentZone('widget.listing.used', 'legal.disclaimer.used')
            ? ContentZonesService.getContentZone('widget.listing.used', 'legal.disclaimer.used').getValue()
            : '';
    };

    const shouldDisplayOrigin = ContentZonesService.getContentZone('widget.listing.used', 'should.display.origin')
        ? ContentZonesService.getContentZone('widget.listing.used', 'should.display.origin').getValue() === 'true'
        : false;

    return (
        <div id={vehicle.vehicleId} className="listing-tile-wrapper">
            <div className="listing-tile" onClick={hasRetail ? onClick : ''}>
                <div className="listing-tile-image">
                    {save}
                    <div role="button" tabIndex={0} className="car-info">
                        <TileTag
                            shouldRender={!vehicle?.isSold() && vehicle.onHold}
                            icon="icon-lock"
                            background="#F5273F"
                            color="#fff"
                            text={LocaleService.t('VEHICLE_STATUS.RESERVED')}
                        />
                        <TileTag
                            shouldRender={!vehicle.isSold() && vehicle.comingSoon} // render this if vehicle is not sold
                            text={LocaleService.t('VEHICLE_STATUS.COMING_SOON')}
                        />
                        <TileTag
                            shouldRender={vehicle.isSold()}
                            icon="icon-lock"
                            background="#F5273F"
                            color="#fff"
                            text={LocaleService.t('VEHICLE_STATUS.SOLD')}
                        />
                        {vehicle?.custom && customTag}
                        <div className="car-meta">#{vehicle.stockNumber}</div>
                        {vehicle.vin && (
                            <div className="car-meta">
                                {LocaleService.t('SUMMARY.VIN_NUMBER', { vin: vehicle.vin })}
                            </div>
                        )}
                        <div className="car-name is-bold">{vehicle.getYearMakeModel()}</div>
                        <div className="car-meta">{vehicle.modelTrim?.name}</div>
                        <div className="car-tagline">{vehicle.tagline}</div>
                    </div>
                    {clearance}
                    <div role="button" tabIndex={0} className="car-image-wrapper">
                        {!isLoading && (
                            <ImageWithFallback
                                imageUrl={vehicleImage}
                                fallbackImageUrl={`${envConfig.source.image}/ir/w360h270/images/${URL.NO_PHOTO}`}
                                className="car-image"
                                alt={vehicle.getYearMakeModel()}
                            />
                        )}
                    </div>
                </div>
                <div className="listing-tile-drivePowerTrains">
                    <div
                        role="button"
                        tabIndex={0}
                        className="listing-tile-drivetrain"
                        aria-label="listing-tile-drivetrain"
                    >
                        <icon className="picto-showroom icon-Front-Wheel-Drive" />
                        <p>{vehicle.driveTrain.name}</p>
                    </div>
                    <div
                        role="button"
                        tabIndex={0}
                        className="listing-tile-transmission"
                        aria-label="listing-tile-transmission"
                    >
                        <icon className="picto-showroom icon-transmission" />
                        <p>{vehicle.transmission.name}</p>
                    </div>
                    <div role="button" tabIndex={0} className="listing-tile-km" aria-label="listing-tile-km">
                        <icon className="picto-showroom icon-km" />
                        <p>{vehicle.getOdometer()}</p>
                    </div>
                </div>

                <div className="listing-tiles-payment">{paymentMethod}</div>

                <div className="listing-tile-footer">
                    <Badges
                        dealerMake={dealerMake}
                        hasCarfaxBadge={hasCarfaxBadge}
                        certifiedText={certifiedText}
                        inspectedText={inspectedText}
                        tradedText={tradedText}
                        warrantyText={warrantyText}
                        certifiedBadge={certifiedBadge}
                        inspectedBadge={inspectedBadge}
                        tradedBadge={tradedBadge}
                        warrantyBadge={warrantyBadge}
                        vehicle={vehicle}
                        showCarfax={showCarfax}
                    />
                    <TileTag
                        shouldRender={vehicle.demo}
                        background="#fff"
                        color="#4b4b4b"
                        text={LocaleService.t('VEHICLE_STATUS.DEMO_TAG')}
                        extraClass="demo-tag"
                    />
                    {shouldDisplayOrigin &&
                        vehicle.primaryOrganizationUnit?.name && (
                            <div className="listing-tile-origin is-light">
                                <icon className="icon-map" />
                                <p>
                                    {LocaleService.t('VEHICLE_TILE.ORIGIN', {
                                        organizationUnitName: vehicle.primaryOrganizationUnit.name,
                                    })}
                                </p>
                            </div>
                        )}
                </div>
            </div>
            <TileDisclaimer uniqueDisclaimer={vehicle.purchaseMethods.disclaimer} czDisclaimer={getCZDisclaimer()} />
        </div>
    );
};

InventoryUsedTile.defaultProps = {
    certifiedText: '',
    inspectedText: '',
    tradedText: '',
    warrantyText: '',
    certifiedBadge: '',
    inspectedBadge: '',
    tradedBadge: '',
    warrantyBadge: '',
    defaultPicture: '',
    dealerMake: '',
    onClick: noop,
    hasCarfaxBadge: false,
    displayCreditRate: false,
    motoInsightUsedEnabled: false,
    roadsterUsedEnabled: false,
    eshopUsedEnabled: false,
    shopClickDriveUsedEnabled: false,
    roadsterLink: '',
    dealerCode: '',
    dealerBAC: '',
};

InventoryUsedTile.propTypes = {
    defaultPicture: PropTypes.string,
    dealerMake: PropTypes.string,
    certifiedText: PropTypes.string,
    inspectedText: PropTypes.string,
    warrantyText: PropTypes.string,
    tradedText: PropTypes.string,
    certifiedBadge: PropTypes.string,
    inspectedBadge: PropTypes.string,
    warrantyBadge: PropTypes.string,
    tradedBadge: PropTypes.string,
    hasCarfaxBadge: PropTypes.bool,
    onClick: PropTypes.func,
    vehicle: PropTypes.instanceOf(VehicleModelV25).isRequired,
    isLoading: PropTypes.bool.isRequired,
    showCarfax: PropTypes.bool.isRequired,
    displayCreditRate: PropTypes.bool,
    motoInsightUsedEnabled: PropTypes.bool,
    roadsterUsedEnabled: PropTypes.bool,
    eshopUsedEnabled: PropTypes.bool,
    shopClickDriveUsedEnabled: PropTypes.bool,
    roadsterLink: PropTypes.string,
    dealerCode: PropTypes.string,
    dealerBAC: PropTypes.string,
};

InventoryUsedTile.displayName = 'Tile-A.InventoryUsedTile';

export default InventoryUsedTile;
